import { RefObject, useEffect, useState } from "react";

/**
 * useActionsFloating — tells a form's bottom action bar whether it should float
 * as a sticky card. Mirrors the bar on /financial/plan/advice/lumpSum (and
 * /information/personal/add-member): the Cancel/Save row sits centred under the
 * form, and only when the page actually has vertical scrolling does it turn into
 * a sticky card at `bottom: 20px` so the buttons stay reachable.
 *
 * Pass a ref to the page wrapper (or form); the hook checks the document and every
 * scrollable ancestor of that node, and recomputes on resize / content changes.
 * `deps` should list state that changes the page height (loaders, toggled sections).
 */
export function useActionsFloating(ref: RefObject<HTMLElement>, deps: any[] = []): boolean {
  const [isFloating, setIsFloating] = useState(false);

  useEffect(() => {
    const isPageScrollable = () => {
      const de = document.documentElement;
      if (de.scrollHeight - de.clientHeight > 1) return true;
      let node: HTMLElement | null = ref.current;
      while (node) {
        const oy = getComputedStyle(node).overflowY;
        if ((oy === "auto" || oy === "scroll") && node.scrollHeight - node.clientHeight > 1) return true;
        node = node.parentElement;
      }
      return false;
    };
    const recompute = () => setIsFloating(isPageScrollable());
    recompute();
    window.addEventListener("resize", recompute);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(recompute);
      if (ref.current) ro.observe(ref.current);
      ro.observe(document.body);
    }
    return () => {
      window.removeEventListener("resize", recompute);
      if (ro) ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return isFloating;
}

export default useActionsFloating;
